import { useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useMe } from '../api';
import { AppIcon } from '../desktop/AppIcon';
import { PageAlert } from '../desktop/Page';
import { appItems, systemItems, type DesktopItem } from '../desktop/items';

/** Chrome OS-style launcher: search on top, big squircle icons underneath. */
export default function Launcher() {
  const { data, isLoading, error } = useMe();
  const [q, setQ] = useState('');
  const apps = useMemo(() => appItems(data), [data]);
  const system = useMemo(() => systemItems(data?.is_admin ?? false).filter((s) => s.to !== '/'), [data?.is_admin]);
  const needle = q.trim().toLowerCase();
  const match = (i: DesktopItem) => !needle || `${i.name} ${i.description ?? ''}`.toLowerCase().includes(needle);
  const shownApps = apps.filter(match);
  const shownSystem = system.filter(match);

  const Tile = ({ item }: { item: DesktopItem }) => {
    const inner = (
      <>
        <AppIcon item={item} size={72} className="lp-icon" />
        <span className="lp-tile-name truncate">{item.name}</span>
        {item.roles && item.roles.length > 0 && <span className="lp-tile-sub">{item.roles.join(' · ')}</span>}
      </>
    );
    return item.href
      ? <a className="lp-tile" href={item.href} title={item.description}>{inner}</a>
      : <Link className="lp-tile" to={item.to!} title={item.description}>{inner}</Link>;
  };

  return (
    <div className="lp">
      <label className="lp-search">
        <Search size={18} strokeWidth={1.8} />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search apps and settings" aria-label="Search apps and settings" autoComplete="off" />
      </label>

      <PageAlert error={error} fallback="Could not load your apps" />

      {!isLoading && !error && apps.length === 0 && !needle && (
        <div className="lp-empty"><h3>No apps yet</h3><p>An administrator needs to grant you a role in at least one app. Your account and settings are still available below.</p></div>
      )}

      {shownApps.length > 0 && (
        <section>
          <h2 className="lp-h">Apps</h2>
          <div className="lp-grid">{shownApps.map((a) => <Tile key={a.id} item={a} />)}</div>
        </section>
      )}
      {shownSystem.length > 0 && (
        <section>
          <h2 className="lp-h">System</h2>
          <div className="lp-grid">{shownSystem.map((s) => <Tile key={s.id} item={s} />)}</div>
        </section>
      )}
      {needle && shownApps.length === 0 && shownSystem.length === 0 && (
        <div className="lp-empty"><h3>No results</h3><p>Nothing matches “{q}”.</p></div>
      )}
    </div>
  );
}
